import { useRouter } from 'next/router';
import { Disclosure, Transition } from '@headlessui/react';
import { matchSorter } from 'match-sorter';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

// Lib
import { getMainMenuSocialProfile } from '@/lib/menu/main';
import type { MainMenuEntryType } from '@/lib/menu/main';
import { INSTAGRAM } from '@/lib/socialProfile';
import { DISCOVER } from '@/routes/privateRoutes';
import mainMenuGroup from '@/lib/menu/mainGroup';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';
import useFormatMenuLink from '@/utils/hooks/menu/useFormatMenuLink';
import useAcl from '@/hooks/user/useAcl';
import { useNewFeatureNotifications } from '@/utils/hooks/user/useNewFeatureNotifications';

// Components
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import Tooltip from '@/components/base/Tooltip';
import Sparkles from '@/components/effects/Sparkles';
import Shining from '@/components/effects/Shining';
import Badge from '@/components/base/Badge';

type SingleMenuEntryProps = {
    hasAccess: boolean;
    item: MainMenuEntryType;
};

const SingleMenuItem = ({ item }: { item: MainMenuEntryType }) => {
    const { t } = useTranslation('commons');

    const { pathname } = useRouter();
    const urlWithoutParams = pathname.split('?')[0];

    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    const isActive =
        item?.link === urlWithoutParams || item?.urlActiveMenu?.includes(urlWithoutParams);

    return (
        <>
            <span className="relative">
                {isNewFeatureOnPages([item?.link, ...(item?.urlActiveMenu ?? [])]) ? (
                    <Sparkles>
                        <Icon
                            icon={item.icon}
                            className={clsx({
                                'text-icoGray-500': !isActive,
                                'text-icoBlue': isActive
                            })}
                        />
                    </Sparkles>
                ) : (
                    <>
                        <Icon
                            icon={item.icon}
                            className={clsx({
                                'text-icoGray-500': !isActive,
                                'text-icoBlue': isActive,
                                'opacity-30': item?.isComingSoon
                            })}
                        />
                        {item?.isComingSoon ? (
                            <div className="absolute -right-1 bottom-0 flex items-center">
                                <Shining>
                                    <Badge variant="coming-soon" iconSize="sm" shorten />
                                </Shining>
                            </div>
                        ) : null}
                    </>
                )}
            </span>
            <span className={clsx('text-sm', { 'opacity-30': item?.isComingSoon })}>
                {t(item.name)}
            </span>
        </>
    );
};

export const SingleMenuEntry = ({
    item,
    hasAccess,
    className
}: SingleMenuEntryProps & { className?: string }) => {
    const { t } = useTranslation('commons');
    const { formatItemLink } = useFormatMenuLink();

    if (hasAccess && !item?.disabled) {
        return item?.isComingSoon ? (
            <div
                className={clsx(
                    'flex w-full cursor-not-allowed items-center space-x-3',
                    className
                )}>
                <SingleMenuItem item={item} />
            </div>
        ) : (
            <LinkBase
                href={formatItemLink(item.link)}
                className={clsx('flex w-full items-center space-x-3', className)}>
                <SingleMenuItem item={item} />
            </LinkBase>
        );
    }

    if (item?.disabled) {
        return (
            <Tooltip
                popperOptions={{
                    //prevent tooltip to pop in the wrong place
                    strategy: 'fixed'
                }}
                content={<span className="w-full">{item?.disabledTooltip}</span>}
                position="right"
                interactive>
                <div className="flex w-full items-center space-x-3">
                    <Icon
                        icon={item.icon}
                        className="text-xl text-icoGray-500 opacity-20 dark:text-icoDarkMode-wolf"
                    />
                    <span className="text-sm opacity-20">{t(item.name)}</span>
                </div>
            </Tooltip>
        );
    }

    return (
        <div className="relative">
            <LinkBase
                href={
                    item.discoverId
                        ? {
                              pathname: DISCOVER,
                              query: { tab: item.discoverId }
                          }
                        : formatItemLink(item.link)
                }
                className="flex w-full items-center justify-between">
                <div className="flex w-full items-center space-x-3">
                    <Icon icon={item.icon} className="text-icoGray-500" />
                    <span className="text-sm opacity-60">{t(item.name)}</span>
                </div>
                <Icon
                    icon="locked"
                    className="text-icoGray-600 rounded-full border-2 border-white bg-gray-200 p-0.5 text-8 text-icoGray-500 dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
                />
            </LinkBase>
        </div>
    );
};

const hasAccess = (item: MainMenuEntryType, hasAcl: (locked: string) => boolean): boolean => {
    if (item.locked) {
        return hasAcl(item.locked);
    }

    return true;
};

const MenuSidebarMain = () => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const isGroup = useIsGroup();
    const { hasAcl } = useAcl();

    const shouldBeDisplayed = (item: MainMenuEntryType): boolean => {
        if (item.shouldBeDisplayed) {
            return hasAcl(item.shouldBeDisplayed);
        }
        return true;
    };

    const network = activeSocialProfile?.social_network_type ?? INSTAGRAM;

    const getMainMenu = () => {
        if (isGroup) {
            return mainMenuGroup;
        }

        return getMainMenuSocialProfile(network);
    };

    const mainMenu = getMainMenu();

    return (
        <div className="space-y-5">
            {mainMenu.map((item, key) => {
                if (!shouldBeDisplayed(item)) {
                    return null;
                }
                if (item.subMenuItems) {
                    return <MenuSidebarMain.Disclosure item={item} key={key} />;
                }

                return (
                    <SingleMenuEntry item={item} hasAccess={hasAccess(item, hasAcl)} key={key} />
                );
            })}
        </div>
    );
};

const SubMenuItem = ({ subMenu }: { subMenu: MainMenuEntryType }) => {
    const { t } = useTranslation('commons');
    const { isNewFeatureOnPages } = useNewFeatureNotifications();

    return (
        <>
            <Icon
                icon={subMenu.icon}
                className={clsx('text-icoBlue', { 'opacity-30': subMenu?.isComingSoon })}
            />
            <span className={clsx('flex-1 text-xs', { 'opacity-30': subMenu?.isComingSoon })}>
                {t(subMenu.name)}
            </span>
            {isNewFeatureOnPages([subMenu?.link, ...(subMenu?.urlActiveMenu ?? [])]) ? (
                <Shining className="rounded-2xl">
                    <Badge variant="new" />
                </Shining>
            ) : null}
            {subMenu?.isComingSoon ? <Badge variant="coming-soon">{t('soon_tag')}</Badge> : null}
        </>
    );
};

const MenuDisclosure = ({ item }: { item: MainMenuEntryType }) => {
    const { pathname } = useRouter();
    const { t } = useTranslation('commons');
    const { hasAcl } = useAcl();
    const { formatItemLink } = useFormatMenuLink();
    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    const urlWithoutParams = pathname.split('?')[0];
    const isActive =
        matchSorter(item.subMenuItems, urlWithoutParams, {
            keys: [
                { threshold: matchSorter.rankings.EQUAL, key: 'link' },
                { threshold: matchSorter.rankings.EQUAL, key: 'urlActiveMenu' }
            ]
        }).length > 0;

    const isNewFeatureItem = item?.subMenuItems
        ? item?.subMenuItems
              .map((subMenuItem: MainMenuEntryType) => [
                  subMenuItem?.link,
                  ...(subMenuItem?.urlActiveMenu ?? [])
              ])
              .flat()
        : [item?.link];

    const isComingSoonFeature = item?.subMenuItems
        ? item?.subMenuItems.filter((subMenuItem: MainMenuEntryType) => subMenuItem?.isComingSoon)
              ?.length > 0
        : item?.isComingSoon;

    return (
        <Disclosure>
            {({ open }) => (
                <>
                    <Disclosure.Button className="flex w-full items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <span className="relative">
                                {(item?.subMenuItems || item?.link) &&
                                isNewFeatureOnPages(isNewFeatureItem) ? (
                                    <Sparkles>
                                        <Icon
                                            icon={open ? item.icon + '-on' : item.icon}
                                            className={clsx({
                                                'text-icoGray-500': !isActive && !open,
                                                'text-icoBlue': isActive || open
                                            })}
                                        />
                                    </Sparkles>
                                ) : (
                                    <>
                                        <Icon
                                            icon={open ? item.icon + '-on' : item.icon}
                                            className={clsx({
                                                'text-icoGray-500': !isActive && !open,
                                                'text-icoBlue': isActive || open
                                            })}
                                        />
                                        {isComingSoonFeature ? (
                                            <div className="absolute -right-1 bottom-0 flex items-center">
                                                <Shining>
                                                    <Badge
                                                        variant="coming-soon"
                                                        iconSize="sm"
                                                        shorten
                                                    />
                                                </Shining>
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </span>
                            <span className="text-sm">{t(item.name)}</span>
                        </div>
                        <Icon
                            icon={open ? 'chevron-top' : 'chevron-bottom'}
                            className="text-xs text-icoGray-500"
                        />
                    </Disclosure.Button>
                    <Transition
                        enter="transition duration-150 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0">
                        <Disclosure.Panel static className="space-y-3.5 pl-3.5">
                            {item.subMenuItems.map((subMenu: MainMenuEntryType) => {
                                const isSubMenuActive =
                                    subMenu.link === urlWithoutParams ||
                                    (subMenu?.urlActiveMenu &&
                                        subMenu.urlActiveMenu.includes(urlWithoutParams));
                                if (hasAccess(subMenu, hasAcl) && subMenu.isComingSoon) {
                                    return (
                                        <div
                                            className="flex w-full cursor-not-allowed items-center space-x-3 text-sm"
                                            key={subMenu.name}>
                                            <SubMenuItem subMenu={subMenu} />
                                        </div>
                                    );
                                }
                                if (hasAccess(subMenu, hasAcl) && !subMenu.isComingSoon) {
                                    return (
                                        <LinkBase
                                            href={formatItemLink(subMenu.link)}
                                            className={clsx(
                                                'flex w-full items-center space-x-3 text-sm hover:text-icoBlue',
                                                {
                                                    'text-icoBlue': isSubMenuActive
                                                }
                                            )}
                                            key={subMenu.name}>
                                            <SubMenuItem subMenu={subMenu} />
                                        </LinkBase>
                                    );
                                }
                                return (
                                    <div className="relative" key={subMenu.name}>
                                        <LinkBase
                                            href={
                                                subMenu.discoverId
                                                    ? {
                                                          pathname: DISCOVER,
                                                          query: { tab: subMenu.discoverId }
                                                      }
                                                    : formatItemLink(subMenu.link)
                                            }
                                            className="flex w-full items-center justify-between">
                                            <div className="flex w-full items-center space-x-3 text-sm">
                                                <Icon
                                                    icon={subMenu.icon}
                                                    className="text-icoGray-500"
                                                />
                                                <span className="text-xs opacity-60">
                                                    {t(subMenu.name)}
                                                </span>
                                            </div>
                                            <Icon
                                                icon="locked"
                                                className="text-icoGray-600 rounded-full border-2 border-white bg-gray-200 p-0.5 text-8 text-icoGray-500 dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
                                            />
                                        </LinkBase>
                                    </div>
                                );
                            })}
                        </Disclosure.Panel>
                    </Transition>
                </>
            )}
        </Disclosure>
    );
};

MenuSidebarMain.Disclosure = MenuDisclosure;

export default MenuSidebarMain;
